import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import SEO from '../components/SEO';

const FAQ = () => {
  const faqCategories = [
    {
      category: 'Algemeen',
      icon: MessageCircle,
      questions: [
        {
          question: 'Voor wie is jullie zorg bedoeld?',
          answer: 'Onze zorg is er voor jeugd van 3 tot 18 jaar met een lichamelijke of verstandelijke beperking, waaronder ASS (Autisme Spectrum Stoornis), ADHD, gedragsproblematiek of een behoefte aan structuur, rust en voorspelbaarheid. We bieden intensieve 1-op-1 begeleiding in een kleinschalige, huiselijke omgeving. Daarnaast bieden we dagbesteding in een klein groepsverband van maximaal 3 jeugdigen, waarbij persoonlijke aandacht, structuur en een veilige omgeving centraal staan.'
        },
        {
          question: 'Wat maakt jullie aanpak uniek?',
          answer: 'Wij zijn betrokken zorgverleners met ruime ervaring in de gehandicaptenzorg. Als (kinder)verpleegkundige bied ik professionele zorg met het hart op de juiste plek. Onze kracht ligt in kleinschaligheid (1-op-1), een huiselijke aanpak en persoonlijke aandacht.'
        },
        {
          question: 'Wat is het verschil tussen dagopvang en logeeropvang?',
          answer: 'Dagopvang is overdag gericht op activiteiten, structuur en ontwikkeling. Logeeropvang biedt daarnaast een veilige slaapplek in onze aanleunwoning voor weekenden, vakanties of doordeweeks. Beide kunnen gecombineerd worden.'
        },
        {
          question: 'Hebben jullie plek voor mijn kind?',
          answer: 'Omdat wij werken met 1-op-1 begeleiding hebben we beperkte plekken beschikbaar. Neem contact met ons op voor een vrijblijvend kennismakingsgesprek om de mogelijkheden te bespreken. We kijken samen naar de best passende oplossing.'
        }
      ]
    },
    {
      category: 'Dagopvang',
      icon: MessageCircle,
      questions: [
        {
          question: 'Wat zijn de openingstijden van de dagopvang?',
          answer: 'Onze dagopvang is geopend van 08:00 tot 20:00 uur. In overleg kunnen de tijden aangepast worden aan jullie specifieke behoeften en het ritme van je kind.'
        },
        {
          question: 'Wat doet mijn kind tijdens de dagopvang?',
          answer: 'Het programma is afhankelijk van de zorgvraag. We bieden een gevarieerd aanbod met afwisseling tussen actieve en passieve momenten, creatieve activiteiten, koken en bakken, educatieve spellen, buitenactiviteiten en prikkelarme momenten. De activiteiten worden afgestemd op de individuele behoeften en interesses van je kind.'
        },
        {
          question: 'Zorgen jullie ook voor de maaltijden?',
          answer: 'Ja, samen met de kinderen bereiden we gezonde en vooral lekkere maaltijden. Hierbij besteden we ook aandacht aan kookvaardigheden. Speciale diëtwensen (bijv. vegetarisch, allergieën) kunnen we makkelijk meenemen.'
        },
        {
          question: 'Wat als mijn kind medicatie nodig heeft?',
          answer: 'Ik ben bevoegd om medicatie te verstrekken en als verpleegkundige ook bevoegd om verpleegtechnische handelingen uit te voeren. Ik volg nauwkeurig alle medische instructies en houd bij wanneer medicatie is gegeven. Dit wordt ook teruggekoppeld naar ouders.'
        },
        {
          question: 'Zijn er ook uitjes en uitstapjes?',
          answer: 'Ja, we organiseren regelmatig uitjes zoals zwemmen, dierentuin, pretpark, kermis en boerderij. Deze worden afgestemd op de behoefte en interesses van het kind.'
        }
      ]
    },
    {
      category: 'Logeeropvang',
      icon: MessageCircle,
      questions: [
        {
          question: 'Wanneer kan mijn kind logeren?',
          answer: 'Logeeropvang is mogelijk in weekenden, tijdens schoolvakanties en doordeweeks (in overleg). We plannen logeerperiodes in overleg met jullie, zodat het goed past in jullie agenda en die van je kind.'
        },
        {
          question: 'Hoe ziet de logeeropvang eruit?',
          answer: 'We hebben een speciaal ingerichte aanleunwoning met eigen woonkamer, slaapkamer, badkamer en keuken. Het is een huiselijke, veilige omgeving waar kinderen zich snel thuis voelen. Alles is ingericht om rust en structuur te bieden.'
        },
        {
          question: 'Wat moet mijn kind meenemen voor het logeren?',
          answer: 'Je kind heeft nodig: eigen kleding voor de dagen, toiletspullen (tandenborstel, shampoo), eventuele medicatie, favoriete knuffel of speelgoed, en alles wat nodig is voor een goede nachtrust (bijv. eigen kussen). We zorgen voor beddengoed.'
        },
        {
          question: 'Hoe blijf ik op de hoogte tijdens het logeren?',
          answer: 'We houden je regelmatig op de hoogte via WhatsApp of telefoon met updates en foto\'s (met toestemming). Bij bijzonderheden nemen we direct contact op. Na het logeren krijg je een uitgebreid verslag van hoe het is gegaan.'
        },
        {
          question: 'Is er 24/7 begeleiding aanwezig?',
          answer: 'Ja, ik ben 24/7 aanwezig en beschikbaar. Er is altijd iemand wakker en alert. Ik slaap in de aangrenzende woning, dus ben direct bereikbaar mocht je kind mij nodig hebben.'
        }
      ]
    },
    {
      category: 'Aanmelding & Financiering',
      icon: MessageCircle,
      questions: [
        {
          question: 'Hoe werkt de aanmelding?',
          answer: 'Stap 1: Neem contact met ons op via telefoon (06-19199023) of email (info@kscare.nl). Stap 2: We plannen een vrijblijvend kennismakingsgesprek en rondleiding. Stap 3: Als het klikt, bespreken we een proefdag/-weekend. Stap 4: Na positieve ervaring starten we met de begeleiding.'
        },
        {
          question: 'Hoe wordt de zorg gefinancierd?',
          answer: 'Onze zorg is toegankelijk via WLZ (Wet langdurige zorg), WMO en Jeugdwet. Tijdens de intake bespreken we welke financieringsvorm het beste past bij jouw situatie. We helpen je graag met de aanvraag.'
        },
        {
          question: 'Heb ik een indicatie nodig?',
          answer: 'Ja, voor onze zorg is een indicatie vereist (WLZ, WMO of Jeugdwet). Als je deze nog niet hebt, kunnen we je adviseren over de aanvraagprocedure. We werken samen met verschillende zorgkantoren en gemeenten.'
        },
        {
          question: 'Kan ik eerst kennismaken voordat ik beslis?',
          answer: 'Absoluut! We nodigen je graag uit voor een vrijblijvend kennismakingsgesprek en rondleiding in onze aanleunwoning. Je kind mag ook meekomen om te kijken en te voelen. Er is geen verplichting om direct te beslissen.'
        }
      ]
    },
    {
      category: 'Veiligheid & Begeleiding',
      icon: MessageCircle,
      questions: [
        {
          question: 'Hoe waarborgen jullie de veiligheid?',
          answer: 'Veiligheid is mijn hoogste prioriteit. Ik werk met 1-op-1 begeleiding, heb een veilig ingerichte ruimte, ben 24/7 bereikbaar, ben gediplomeerd verpleegkundige, en volg alle wettelijke richtlijnen voor jeugdzorg.'
        },
        {
          question: 'Wat is jullie ervaring met moeilijk verstaanbaar gedrag?',
          answer: 'Ik heb ruime ervaring met moeilijk verstaanbaar gedrag (MVG). Ik gebruik positieve gedragsondersteuning en werk nauw samen met ouders om gedrag te begrijpen en passend te begeleiden.'
        },
        {
          question: 'Hoe gaan jullie om met agressie of crisis?',
          answer: 'Ik ben getraind in crisisinterventie en de-escalatietechnieken. Mijn aanpak is altijd gericht op preventie door structuur en duidelijkheid. Bij crisis blijf ik rustig, bied veiligheid en zoek naar de onderliggende oorzaak. Ik evalueer elke situatie en pas de aanpak aan.'
        },
        {
          question: 'Ben je BIG-geregistreerd?',
          answer: 'Ja, ik ben BIG-geregistreerd (kinder)verpleegkundige. Ik volg regelmatig bij- en nascholing om mijn kennis up-to-date te houden.'
        },
        {
          question: 'Wat gebeurt er bij een noodgeval?',
          answer: 'Ik heb een duidelijk noodprotocol. Bij medische noodgevallen schakel ik direct 112 in en informeer ik de ouders. Ik heb EHBO-certificaten en kan eerste hulp verlenen. Alle noodcontacten zijn altijd bij de hand.'
        }
      ]
    },
    {
      category: 'Praktisch',
      icon: MessageCircle,
      questions: [
        {
          question: 'Waar zijn jullie gevestigd?',
          answer: 'We zijn gevestigd nabij Sittard, in de omgeving van Nieuwstadt. Het exacte adres delen we tijdens de intake. We zijn goed bereikbaar met auto en openbaar vervoer.'
        },
        {
          question: 'Verzorgen jullie ook vervoer?',
          answer: 'In overleg kunnen we vervoer regelen of begeleiden. Dit wordt per situatie bekeken en is afhankelijk van de afstand en de mogelijkheden. Veel ouders brengen en halen zelf, wat ook een fijn moment is voor overdracht.'
        },
        {
          question: 'Kunnen broertjes/zusjes ook mee?',
          answer: 'Maatwerk - afhankelijk van de zorgvraag is het mogelijk. Wel kan er tijdens een kennismakingsgesprek het hele gezin komen kijken.'
        },
        {
          question: 'Werken jullie met andere zorgaanbieders samen?',
          answer: 'Ja, we werken graag samen met andere betrokken professionals zoals scholen, therapeuten, gedragsdeskundigen en artsen. Goede afstemming is belangrijk voor optimale zorg. Met toestemming wisselen we informatie uit.'
        },
        {
          question: 'Wat als mijn kind ziek wordt?',
          answer: 'In overleg zoeken we een passende oplossing. Bij chronische aandoeningen of medicatiegebruik kunnen we dit goed begeleiden. We overleggen altijd vooraf over medische zaken.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="FAQ - Veelgestelde Vragen"
        description="Antwoorden over dagopvang, logeeropvang, aanmelding en financiering (WLZ, WMO, Jeugdwet). Lees onze FAQ of bel 06-19199023."
        keywords="FAQ, veelgestelde vragen, dagopvang, logeeropvang, aanmelding, WLZ, financiering"
        
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              className="text-5xl md:text-6xl font-bold text-[#1A4D4D] mb-6"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              Veelgestelde Vragen
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Alle antwoorden op je vragen over onze dagbesteding en logeeropvang. 
              Staat je vraag er niet bij? Neem gerust contact met ons op!
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFE8E0] to-[#FFD0C0] flex items-center justify-center">
                    <category.icon className="w-6 h-6 text-[#FF6347]" />
                  </div>
                  <h2 
                    className="text-3xl font-bold text-[#1A4D4D]"
                    style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
                  >
                    {category.category}
                  </h2>
                </div>

                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((item, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`${categoryIndex}-${index}`}
                      className="border border-gray-200 rounded-2xl px-6 bg-gray-50 hover:bg-white transition-all duration-300"
                    >
                      <AccordionTrigger className="text-left hover:no-underline py-6">
                        <span className="text-lg font-semibold text-[#1A4D4D] pr-4">
                          {item.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 leading-relaxed pb-6">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl p-12 text-center">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A4D4D] mb-4"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              Staat je vraag er niet bij?
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              We beantwoorden graag al je vragen persoonlijk. 
              Neem gerust contact met ons op via telefoon of email.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                className="bg-[#FF6347] hover:bg-[#FF5533] text-white rounded-full px-8 py-6 text-lg"
                onClick={() => window.location.href = 'tel:0619199023'}
              >
                <Phone className="w-5 h-5 mr-2" />
                Bel 06-19199023
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-[#1A4D4D] text-[#1A4D4D] hover:bg-[#1A4D4D] hover:text-white rounded-full px-8 py-6 text-lg"
                onClick={() => window.location.href = 'mailto:info@kscare.nl'}
              >
                <Mail className="w-5 h-5 mr-2" />
                Email ons
              </Button>
            </div>

            <div className="pt-8 border-t border-gray-200">
              <p className="text-gray-600 mb-4">
                Of plan een vrijblijvende kennismaking
              </p>
              <a href="mailto:info@kscare.nl?subject=Intake%20aanvraag">
                <Button 
                  className="bg-[#1A4D4D] hover:bg-[#0F3838] text-white rounded-full px-8 py-6 text-lg"
                >
                  Plan een intake
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
